/**
 * Aura (Foundry: rule-element/aura.ts): an aura the Character emits, with the Effects it carries.
 * Only the bearer's side is in scope (docs/design.md): an Effect that includes the bearer is
 * applied to the Character in memory while the aura's predicate passes, as Foundry does when the
 * bearer's token stands in its own aura. Propagation to other tokens is canvas work.
 */
import { applyAlteration, parseAlterations, type ItemAlteration } from "../alterations";
import { EngineItem } from "../items";
import { isValidPredicate, testPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import type { AuraEffectData } from "../synthetics";
import type { RuleHandler, RuleInstance } from "./base";
import { isItemUuid } from "./choice-set";

interface AuraEffectSource {
  uuid: string;
  affects: "allies" | "enemies" | "all";
  events: ("enter" | "turn-start" | "turn-end")[];
  save: { type: string; dc: unknown } | null;
  predicate: RawPredicate;
  removeOnExit: boolean;
  includesSelf: boolean;
  alterations: ItemAlteration[];
}

interface AuraData {
  slug: string;
  radius: unknown;
  level: unknown;
  traits: string[];
  effects: AuraEffectSource[];
  mergeExisting: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const rawEffects = Array.isArray(s.effects) ? s.effects : [];
  const effects: AuraEffectSource[] = [];
  const allNotEnemies = rawEffects.every((e) => !isPlainObject(e) || e.affects !== "enemies");
  for (const raw of rawEffects) {
    if (!isPlainObject(raw) || typeof raw.uuid !== "string" || !raw.uuid) return rule.fail("effects: each effect needs a uuid");
    const affects = raw.affects === undefined ? "all" : raw.affects;
    if (affects !== "allies" && affects !== "enemies" && affects !== "all") return rule.fail(`effects: "${String(affects)}" is not allies, enemies or all`);
    const events = Array.isArray(raw.events) ? raw.events.filter((e): e is "enter" | "turn-start" | "turn-end" => e === "enter" || e === "turn-start" || e === "turn-end") : ["enter" as const];
    if (raw.predicate !== undefined && !isValidPredicate(raw.predicate)) return rule.fail("effects: malformed predicate");
    const { alterations, error } = parseAlterations(raw.alterations);
    if (error) return rule.fail(error);
    const save = isPlainObject(raw.save) && typeof raw.save.type === "string" ? { type: raw.save.type, dc: cleanRuleValue(raw.save.dc) ?? 0 } : null;
    effects.push({
      uuid: raw.uuid,
      affects,
      events,
      save,
      predicate: (raw.predicate as RawPredicate | undefined) ?? [],
      removeOnExit: typeof raw.removeOnExit === "boolean" ? raw.removeOnExit : events.includes("enter"),
      includesSelf: typeof raw.includesSelf === "boolean" ? raw.includesSelf : allNotEnemies,
      alterations,
    });
  }
  rule.data = {
    slug: rule.slug ?? rule.item.slug ?? sluggify(rule.item.name),
    radius: s.radius === undefined ? 5 : cleanRuleValue(s.radius),
    level: s.level === undefined || s.level === null ? null : cleanRuleValue(s.level),
    traits: Array.isArray(s.traits) ? s.traits.filter((t): t is string => typeof t === "string" && t !== "aura") : [],
    effects,
    mergeExisting: s.mergeExisting !== false,
  } satisfies AuraData as unknown as Record<string, unknown>;
}

/** The bearer as an aura origin: its `self:` options as `origin:` options. */
function originOptions(state: EngineState): string[] {
  return [...state.rollOptions.get()].filter((o) => o.startsWith("self:")).map((o) => o.replace(/^self:/, "origin:"));
}

function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  if (!rule.test(state)) return;
  const data = rule.data as unknown as AuraData;
  const radius = Math.min(240, Math.max(5, Math.ceil(Number(resolveValue(rule, state, data.radius)) / 5) * 5));
  if (rule.ignored || !Number.isInteger(radius) || radius <= 0) return;
  const resolvedLevel = data.level === null ? null : resolveValue(rule, state, data.level, null, { warn: false });
  const level = typeof resolvedLevel === "number" ? Math.trunc(resolvedLevel) : rule.item.type === "effect" ? rule.item.level : null;
  const selfOptions = new Set([...state.rollOptions.get(), ...originOptions(state)]);
  const effects: AuraEffectData[] = [];
  for (const effect of data.effects) {
    const uuid = resolveInjectedProperties(rule, state, effect.uuid, { warn: false });
    if (!isItemUuid(uuid)) {
      rule.fail(`Unable to resolve effect uuid: ${effect.uuid}`);
      return;
    }
    const entry = state.entries.get(uuid) ?? null;
    const record = entry ? null : (state.catalogue?.byUuid(uuid) ?? null);
    const type = entry?.type ?? record?.t ?? null;
    if (type && type !== "effect" && type !== "affliction") rule.fail('effects transmitted by auras must be of type "effect" or "affliction"');
    const predicate = resolveInjectedProperties(rule, state, structuredClone(effect.predicate), { warn: false });
    const wantsSelf = effect.includesSelf && effect.affects !== "enemies" && testPredicate(predicate, selfOptions);
    let appliedToSelf = false;
    if (wantsSelf) {
      if (!entry) {
        state.needed.add(uuid);
        rule.problems.push(`aura Effect ${uuid} is not loaded`);
      } else if (!state.items.some((i) => i.grantedBy === rule.item && i.uuid === uuid && i.role === "temporary")) {
        const item = new EngineItem({ entry, grantedBy: rule.item });
        item.inMemory = true;
        item.specialOptions.push("origin", "origin:self");
        for (const alteration of effect.alterations) {
          const problem = applyAlteration(rule, state, item, alteration);
          if (problem) rule.problems.push(problem);
        }
        state.items.push(item);
        state.addRules(item);
        appliedToSelf = true;
      } else {
        appliedToSelf = true;
      }
    }
    effects.push({ uuid, name: entry?.name ?? record?.n ?? null, affects: effect.affects, events: [...effect.events], save: effect.save ? { type: effect.save.type, dc: typeof effect.save.dc === "number" ? effect.save.dc : String(effect.save.dc) } : null, predicate, removeOnExit: effect.removeOnExit, includesSelf: effect.includesSelf, appliedToSelf });
  }
  if (rule.ignored) return;
  const auras = state.synthetics.auras;
  const existing = auras.get(data.slug);
  if (existing && data.mergeExisting) {
    existing.radius = radius;
    existing.traits = [...new Set([...existing.traits, ...data.traits])].sort();
    for (const effect of effects) {
      const index = existing.effects.findIndex((e) => e.uuid === effect.uuid);
      if (index !== -1) existing.effects.splice(index, 1, effect);
      else existing.effects.push(effect);
    }
  } else {
    auras.set(data.slug, { slug: data.slug, radius, level, traits: [...new Set(data.traits)].sort(), effects, source: rule.item.uuid, sourceName: rule.item.name });
  }
}

export const AuraHandler: RuleHandler = { key: "Aura", parse, onApplyActiveEffects };
